import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import { blogPublic } from "../../lib/api";
import { Btn } from "../../lib/Btn";
import { Icon } from "../../lib/Icon";
import { Pill } from "../../lib/Pill";
import { Slats } from "../../lib/Slats";
import { renderMarkdown } from "../../lib/markdown";
import { articleJsonLd, useSeo } from "../../lib/useSeo";

export function BlogPostPage() {
  const { slug } = useParams<{ slug: string }>();
  const post = useQuery({
    queryKey: ["blog", "show", slug],
    queryFn: () => blogPublic.show(slug!),
    enabled: !!slug,
  });
  const p = post.data;
  useSeo({
    title: p ? `${p.title} · NestPlot` : "Loading post · NestPlot",
    description: p
      ? p.meta_description || p.excerpt || `Read ${p.title} on the NestPlot blog.`
      : "Loading post.",
    path: `/blog/${slug ?? ""}`,
    ogType: p ? "article" : "website",
    image: p?.cover_image_url ?? undefined,
    jsonLd: p
      ? articleJsonLd({
          headline: p.title,
          description: p.meta_description || p.excerpt || "",
          path: `/blog/${slug ?? ""}`,
          datePublished: (p.published_at as unknown as string) ?? new Date().toISOString(),
          image: p.cover_image_url,
          authorName: p.author ?? undefined,
        })
      : undefined,
  });

  if (post.isLoading) {
    return (
      <div className="mx-auto max-w-[760px] px-5 lg:px-8 py-16 text-center text-sub">
        Loading…
      </div>
    );
  }
  if (post.isError || !p) {
    return (
      <div className="mx-auto max-w-[760px] px-5 lg:px-8 py-16 text-center">
        <h1 className="t-display-l text-alert">Post not found</h1>
        <Link to="/blog" className="text-accent font-semibold mt-3 inline-block">
          Back to blog
        </Link>
      </div>
    );
  }

  return (
    <article className="mx-auto max-w-[760px] px-5 lg:px-8 py-12 lg:py-16">
      <Link
        to="/blog"
        className="inline-flex items-center gap-2 text-sub text-[13px] font-semibold hover:text-ink mb-6"
      >
        <Icon name="arrow-left" size={14} /> All posts
      </Link>
      <Slats color="var(--accent)" height={12} bands={4} style={{ width: 64, marginBottom: 18 }} />
      <div className="flex items-center gap-2 mb-3">
        <Pill tone="default" size="sm">
          {p.post_type}
        </Pill>
        {p.published_at && (
          <span className="text-sub text-xs font-mono">
            {new Date(p.published_at).toLocaleDateString(undefined, {
              month: "long",
              day: "numeric",
              year: "numeric",
            })}
          </span>
        )}
        {p.author && <span className="text-sub text-xs">· by {p.author}</span>}
      </div>
      <h1 className="t-display-l mb-4">{p.title}</h1>
      {p.excerpt && (
        <p className="text-sub text-lg leading-relaxed mb-8">{p.excerpt}</p>
      )}
      {p.cover_image_url && (
        <img
          src={p.cover_image_url}
          alt={p.title}
          loading="eager"
          className="w-full h-auto rounded-xl border border-line mb-10"
        />
      )}
      {/* Markdown → safe HTML. Renderer escapes input before applying inline
          syntax, so raw HTML in posts is shown as text. */}
      <div
        className="prose-nestplot text-[16px] leading-[1.7] text-ink"
        dangerouslySetInnerHTML={{ __html: renderMarkdown(p.content) }}
      />


      <div className="mt-12 grid md:grid-cols-[1.4fr_1fr] gap-6 items-center border-t border-line pt-8">
        <div>
          <h3 className="font-display font-extrabold text-[20px] uppercase tracking-[-0.01em]">
            Liked this?
          </h3>
          <p className="text-sub text-sm mt-2">
            One email a week, the posts that matter, no spam.
          </p>
        </div>
        <Link to="/blog">
          <Btn kind="primary" size="md" full iconRight={<Icon name="arrow-right" size={14} />}>
            Subscribe
          </Btn>
        </Link>
      </div>
    </article>
  );
}

